import { useCallback, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import type { OutputFrameT } from '../../image';
import type { TrackingValidationResultT } from '../components/review-suggestions-panel/types';
import { BASE_API_URL } from '../consts/base-url';
import type { EventPayload } from '../observability/types';
import customFetch from '../utils/fetch';
const ML_SUGGESTIONS_VALIDATION_LATENCY = 'ml_suggestions_validation_latency';
const useValidateTrackingDetails = ({
  taskId,
  getFrames,
  startFrameIndex = 0,
  onEvaluate,
  sendEvent
}: {
  taskId: number;
  getFrames: () => OutputFrameT[];
  startFrameIndex: number;
  onEvaluate: (isSuccessfullyValidated: boolean) => void;
  sendEvent?: (payload: EventPayload) => void;
}) => {
  const {
    isPending,
    error,
    data,
    mutateAsync
  } = useMutation({
    mutationKey: ['validateTrackingInconsistencies'],
    mutationFn: async ({
      taskId,
      frames,
      startFrameNumber = 0
    }: {
      taskId: number;
      frames: OutputFrameT[];
      startFrameNumber: number;
    }) => {
      const response = await customFetch(`${BASE_API_URL}/video/validate`, {
        method: 'POST',
        body: JSON.stringify({
          taskId,
          startFrameNumber,
          frames
        }),
        headers: {
          'Content-Type': 'application/json'
        }
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to validate tracking inconsistencies');
      }
      return response.json();
    }
  });
  const [trackingDetails, setTrackingDetails] = useState<TrackingValidationResultT[] | null>(data ?? null);
  const [trackingDetailsError, setTrackingDetailsError] = useState<Error | null>(error ?? null);
  const [trackingDetailsLoading, setTrackingDetailsLoading] = useState<boolean>(isPending);
  const validateTrackingDetails = useCallback(async () => {
    const frames = getFrames();
    setTrackingDetailsLoading(true);
    setTrackingDetailsError(null);
    const startTime = Date.now();
    try {
      await mutateAsync({
        taskId,
        frames,
        startFrameNumber: startFrameIndex
      }, {
        onSuccess: data => {
          const latencyMs = Date.now() - startTime;
          sendEvent?.({
            action: ML_SUGGESTIONS_VALIDATION_LATENCY,
            latencyMs,
            taskId,
            frameCount: frames.length,
            success: !data.error
          });
          if (data.data?.result) {
            setTrackingDetails(data.data?.result);
            onEvaluate?.(true);
          }
          if (data.error) {
            setTrackingDetailsError(new Error(data.error));
            onEvaluate?.(false);
          }
          setTrackingDetailsLoading(false);
        },
        onError: error => {
          const latencyMs = Date.now() - startTime;
          sendEvent?.({
            action: ML_SUGGESTIONS_VALIDATION_LATENCY,
            latencyMs,
            taskId,
            frameCount: frames.length,
            success: false,
            error: error.message
          });
          setTrackingDetailsError(error);
          setTrackingDetailsLoading(false);
          onEvaluate?.(false);
        }
      });
    } catch (error) {
      const latencyMs = Date.now() - startTime;
      sendEvent?.({
        action: ML_SUGGESTIONS_VALIDATION_LATENCY,
        latencyMs,
        taskId,
        frameCount: frames.length,
        success: false,
        error: (error as Error).message
      });
      setTrackingDetailsError((error as Error));
      setTrackingDetailsLoading(false);
      onEvaluate?.(false);
    }
  }, [mutateAsync, setTrackingDetails, setTrackingDetailsError, setTrackingDetailsLoading, taskId, getFrames, startFrameIndex, sendEvent, onEvaluate]);
  return {
    trackingDetailsLoading,
    trackingDetailsError,
    trackingDetails,
    validateTrackingDetails,
    setTrackingDetails
  };
};
export default useValidateTrackingDetails;